import React, { useState, useEffect, useRef } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "react-select";
import CustomModal from "./CustomModal";
import { Box } from "@mui/material";
import { SendEmail } from "../../../api/report";
import GeneralButton from "../SaveButton/GeneralButton";
import { useSelector } from "react-redux";
import CrossIcon from "../../../images/cross.svg";
import images from "../../../utils/images";
import {
  showSuccess,
  showError,
  showErrorMsg,
} from "../../../utils/showMessage";
import "./ExportModal.scss";
import { Form, Row, Col } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { handleEmailKeyDown } from "../regex/Regex";
import { use } from "react";
import { useMemo } from "react";
import { FetchGroupNames } from "../../../api/groups";
import CreatableSelect from "react-select/creatable";
import { LazyLoadImage } from "react-lazy-load-image-component";
import {
  getProperReportName,
  formatReportType,
} from "../../dashboard/Reports/getReportsData";
import { useReactSelectTheme } from "../../../utils/useReactSelectTheme";

const formatOptions = [
  { value: "csv", label: "CSV" },
  { value: "pdf", label: "PDF" },
  { value: "both", label: "BOTH" },
];

const exportTypeOptions = [
  { value: "csv", label: "Export CSV" },
  { value: "pdf", label: "Export PDF" },
  { value: "email", label: "Send Email" },
];

export const ExportModal = ({
  open,
  handleClose,
  getData,
  search,
  pageType,
  filterBy,
  advanceFilters,id
}) => {
  const [exportType, setExportType] = useState("csv");
  const [emailFormat, setEmailFormat] = useState("csv");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [groupNames, setGroupNames] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [type, setType] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const firstInputRef = useRef(null);

  

  const capabilities = useSelector((state) => state.user?.user?.role?.capabilities || {});
  const isCapable = 'is_admin_user' in capabilities ? capabilities : false;
  const hasExportGroupsRolesDataCapability = isCapable?.report?.export_groups_roles_data;

  const hidePdfForReports = ['app_executed_commands_audit', 'asset_executed_commands_audit','epm_user_action_audit'];
  const hideEmailForReports = ['epm_user_action_audit'];
  const shouldHidePdf = hidePdfForReports.includes(pageType);
  const shouldHideEmail = hideEmailForReports.includes(pageType);

  useEffect(() => {
    if (open && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [open]);

  const components = {
    DropdownIndicator: null,
  };
  
  const { styles: themeSelectStyles } = useReactSelectTheme();
  
  const selectStyles = useMemo(
    () => ({
      ...themeSelectStyles,
      control: (styles) => ({
        ...themeSelectStyles.control(styles),
        paddingTop: "6px",
        paddingBottom: "6px",
        boxShadow: "none",
        fontFamily: '"DM Sans"',
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "21px",
        ":hover": {
          ...themeSelectStyles.control(styles)[":hover"],
          borderColor: themeSelectStyles.control(styles).borderColor,
        },
      }),
      option: (styles, state) => ({
        ...themeSelectStyles.option(styles, state),
        fontFamily: '"DM Sans"',
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "21px",
      }),
      menu: (provided) => ({
        ...themeSelectStyles.menu(provided),
        maxHeight: "200px",
        overflowY: "auto",
        padding: 0,
      }),
      menuList: (styles) => ({
        ...themeSelectStyles.menuList(styles),
        maxHeight: "200px",
        overflowY: "auto",
        padding: "0",
      }),
      multiValue: (styles) => ({
        ...themeSelectStyles.multiValue(styles),
      }),
      multiValueLabel: (styles) => ({
        ...themeSelectStyles.multiValueLabel(styles),
      }),
      multiValueRemove: (styles) => ({
        ...themeSelectStyles.multiValueRemove(styles),
      }),
      placeholder: (styles) => ({
        ...themeSelectStyles.placeholder(styles),
      }),
      input: (styles) => ({
        ...themeSelectStyles.input(styles),
      }),
    }),
    [themeSelectStyles]
  );

  useEffect(() => {
    const groupDetails = async () => {
      // Only fetch groups if user has export_groups_roles_data capability
      if (hasExportGroupsRolesDataCapability) {
        const name = "group";
        try {
          const { data } = await FetchGroupNames(name);
          const formattedGroups = data ? data.map((group) => ({
            label: group,
            value: group,
          })) : [];
          setGroupNames(formattedGroups);
        } catch (error) {
          setGroupNames([]);
        }
      } else {
        setGroupNames([]);
      }
    };

    if (open) {
      groupDetails();
    }
  }, [open, hasExportGroupsRolesDataCapability]);

  useEffect(() => {
    const roleDetails = async () => {
      // Only fetch roles if user has export_groups_roles_data capability
      if (hasExportGroupsRolesDataCapability) {
        const name = "role";
        try {
          const { data } = await FetchGroupNames(name);
          const formattedGroups = data ? data.map((role) => ({
            label: role,
            value: role,
          })) : [];
          setRoleNames(formattedGroups);
        } catch (error) {
          setRoleNames([]);
        }
      } else {
        setRoleNames([]);
      }
    };

    if (open) {
      roleDetails();
    }
  }, [open, hasExportGroupsRolesDataCapability]);

  useEffect(() => {
    if (open) {
      setExportType("csv");
      setEmailFormat("csv");
      setSelectedEmails([]);
      setInputValue(""); 
      setValidationError("");
      setGroupNames(null);
      setRoleNames(null);
    }
  }, [open]);

  useEffect(() => {
    if (
      selectedGroups &&
      selectedGroups.length >= 1 &&
      selectedRoles &&
      selectedRoles.length >= 1
    ) {
      setType(["group", "role"]);
    } else if (selectedGroups && selectedGroups.length >= 1) {
      setType(["group"]);
    } else if (selectedRoles && selectedRoles.length >= 1) {
      setType(["role"]);
    } else {
      setType([]);
    }
  }, [selectedGroups, selectedRoles]);

  useEffect(() => {
    if (
      selectedGroups.length ||
      selectedRoles.length ||
      selectedEmails.length
    ) {
      setValidationError("");
    }
  }, [selectedGroups, selectedRoles, selectedEmails]);

  const handleSubmit = async () => {
    setLoading(true);

    if (!exportType) {
      setValidationError("Please select one of the export options.");
      setLoading(false);
      return;
    }

    if (exportType === "email") {
      if (shouldHideEmail) {
        setValidationError("Email option is not available for this report type.");
        setLoading(false);
        return;
      }
    
      if (inputValue && inputValue.trim()) {
        
        handleKeyDownWrapper({ key: "Enter", preventDefault: () => {} });

        if (inputValue && inputValue.trim()) {
          setValidationError("Please enter a valid email address.");
          setLoading(false);
          return;
        }
      }

      if (
        !selectedEmails.length &&
        !selectedGroups.length &&
        !selectedRoles.length
      ) {
        setValidationError(
          "Please specify at least one email or select either group or role."
        );
        setLoading(false);
        return;
      }
    }

    try {
      if (exportType === "email") {
        const emailValues = selectedEmails.map((email) => email.value);
        const { data, error } = await SendEmail(
          emailValues,
          emailFormat,
          pageType,
          advanceFilters,
          search,
          filterBy,
          selectedGroups,
          selectedRoles,
          type,"","","",id
        );

        if (error) {
          const exception = error.message[1].replace(/[\[\]']+/g, '');
          showErrorMsg(error.message[0]+' '+exception);
        } else if (data !== null) {
          showSuccess(data.message);
        }
      } else {
        // For CSV/PDF exports, let fetchData handle the download and error message
        await getData(
          null,
          null,
          search,
          pageType,
          exportType,
          filterBy,
          advanceFilters
        );
      }
    } catch (error) {
      showError("Failed to complete the export action. Please try again.");
    } finally {
      setLoading(false);
      if (!validationError) {
        handleClose();
      }
    }
    setSelectedGroups([]);
    setSelectedRoles([]);
    setType([]);
  };

  const createOption = (label) => ({
    label,
    value: label,
  });

  const handleKeyDownWrapper = (event) => {
    handleEmailKeyDown(
      event,
      inputValue,
      setInputValue,
      selectedEmails,
      setSelectedEmails,
      setValidationError,
      createOption
    );
  };

  const handleExportTypeChange = (value) => {
    setExportType(value);
    setValidationError("");

    if (value !== "email") {
      setEmailFormat("csv");
      setSelectedEmails([]);
      setInputValue(""); 
    }
  };

  const handleSelectChange = (selected) => {
    setSelectedEmails(
      selected.map((email) => ({ label: email.label, value: email.value }))
    );
  };


  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title="Export Options"
      customStyles={{ height: "auto" }}
    >
      <div className="export-modal">
        <Container
          fluid
          className=" mx-auto add_systemuser_container"
        >
          <LazyLoadImage
            src={CrossIcon}
            alt="close"
            className="close-icon"
            onClick={handleClose}
          />

          <h2 className="main_content_heading">Choose an Export Option</h2>
          <RadioGroup
            row
            value={exportType}
            onChange={(e) => handleExportTypeChange(e.target.value)}
            className="radio-group"
            size="small"
          >
            <Box display="flex" gap={1} mt={2}>
              {exportTypeOptions
                .filter((option) => {
                  const hidePdf = shouldHidePdf && option.value === "pdf";
                  const hideEmail = shouldHideEmail && option.value === "email";
                  return !hidePdf && !hideEmail;
                })
                .map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
            </Box>
          </RadioGroup>

          {exportType === "email" && !shouldHideEmail && (
            <Row className="pt-3">
              <Col md={2} className="d-flex align-items-center">
                <label className="input_lable">Email Report as</label>
              </Col>
              <Col md={7}>
                <Select
                  options={formatOptions.filter(
                    (option) =>
                      !shouldHidePdf ||
                      (option.value !== "pdf" && option.value !== "both")
                  )}
                  onChange={(option) => setEmailFormat(option.value)}
                  placeholder="Select Format"
                  value={formatOptions.find(
                    (option) => option.value === emailFormat
                  )}
                  styles={selectStyles}
                />
              </Col>
            </Row>
          )}

          {exportType === "email" && !shouldHideEmail && (
            <div>
              <Row className="pt-3">
                <Col md={2} className="d-flex align-items-center">
                  <label className="input_lable">Groups</label>
                </Col>
                <Col md={7}>
                  <Select
                    options={groupNames}
                    className="p-0 form_input_field"
                    isMulti
                    isDisabled={!hasExportGroupsRolesDataCapability}
                    placeholder={hasExportGroupsRolesDataCapability ? "Select Groups" : "Groups (No Access)"}
                    onChange={(selected) =>
                      setSelectedGroups(selected.map((group) => group.value))
                    }
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              <Row className="pt-3">
                <Col md={2} className="d-flex align-items-center">
                  <label className="input_lable">Roles</label>
                </Col>
                <Col md={7}>
                  <Select
                    options={roleNames}
                    className="p-0 form_input_field"
                    isMulti
                    isDisabled={!hasExportGroupsRolesDataCapability}
                    placeholder={hasExportGroupsRolesDataCapability ? "Select Roles" : "Roles (No Access)"}
                    onChange={(selected) =>
                      setSelectedRoles(selected.map((role) => role.value))
                    }
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              <Row className="pt-3">
                <Col md={2} className="d-flex align-items-center">
                  <label className="input_lable">Specify Emails</label>
                </Col>
                <Col md={7}>
                  <CreatableSelect
                    className="form_input_field"
                    isMulti
                    isClearable
                    components={components}
                    inputValue={inputValue}
                    menuIsOpen={false}
                    placeholder="Enter the emails and press enter"
                    onChange={handleSelectChange}
                    onInputChange={(newValue, actionMeta) => {
                      if (actionMeta.action !== "input-blur" &&actionMeta.action !== "menu-close") {
                        setInputValue(newValue);
                        if (!newValue.trim()) {
                          setValidationError("");
                        }
                      }
                    }}
                    onKeyDown={handleKeyDownWrapper}
                    onBlur={() => {
                      if (inputValue && inputValue.trim()) {
                        handleKeyDownWrapper({
                          key: "Enter",
                          preventDefault: () => {},
                        });
                      }
                    }}
                    value={selectedEmails}
                    styles={selectStyles}
                    autoComplete="on"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </Col>
              </Row>
            </div>
          )}

          {validationError && (
            <div className="error-message">{validationError}</div>
          )}

          <Row className="pt-3 mb-3 justify-content-end">
            <Col sm="auto">
              <GeneralButton
                onClickEvent={handleClose}
                className="me-1"
                value="Cancel"
                color="#505050"
                variant="outlined"
                size="large"
              />
            </Col>
            <Col sm="auto" className="me-sm-2 p-0">
              {loading ? (
                <GeneralButton
                  variant="contained"
                  disabled={true}
                  className="me-1"
                  value={
                    <LazyLoadImage
                      src={images.Loader}
                      width="26px"
                      height="26px"
                      alt="loading"
                    />
                  }
                  size="large"
                />
              ) : (
                <GeneralButton
                  className="me-1"
                  onClickEvent={handleSubmit}
                  value={exportType === "email" ? "Send Email" : "Export"}
                  variant="contained"
                  size="large"
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </CustomModal>
  );
};
